import './assets/fonts/inter.css'
import './assets/main.css'

// Bootstrap icons (font only – no Bootstrap CSS/JS)
import 'bootstrap-icons/font/bootstrap-icons.css'

// Override font-display: block → swap for PageSpeed (FOIT fix)
import './assets/font-display-override.css'

// Custom UI system – full Bootstrap CSS replacement
import './assets/admin-ui.css'

// Lightweight Bootstrap JS replacement (no external dependency)
import './utils/bootstrap-compat'

// Gallery CSS imports
import './assets/css/gallery-organic.css'
import './assets/css/gallery-3d.css'
import './assets/css/gallery-glitch.css'

// Widget style imports
import './assets/css/project-cards.css'

// Widget animations
import './assets/widget-animations.css'

// Column widget responsive layout
import './assets/css/column-widgets-responsive.css'
import './assets/css/widget-mobile-responsive.css'

// CRITICAL: Custom CSS overrides - MUST load AFTER Bootstrap
import './assets/custom-overrides.css'

import { createApp } from 'vue'
import { createPinia } from 'pinia'

import App from './App.vue'
import router from './router'
import { settingsService } from './services/api'
import { getLogoUrl } from './config/env'
import { useUserStore } from './stores/user'
import Vue3SocialSharing from 'vue3-social-sharing'
import { subscribeToPush } from './services/push'

// Initialize analytics tracking
import './utils/analytics'

// Global error handler to filter out external errors
const setupGlobalErrorHandler = () => {
  // List of external domains to ignore
  const ignoredDomains = [
    'facebook.com',
    'fbcdn.net',
    'connect.facebook.net',
    'google-analytics.com',
    'googletagmanager.com',
    'gstatic.com',
    'googleapis.com',
    'cdn.jsdelivr.net',
    'unpkg.com'
  ]

  // Override console.error to filter external errors
  const originalError = console.error
  console.error = function(...args: any[]) {
    const errorString = args.join(' ')
    
    // Check if error is from ignored domain
    const isExternalError = ignoredDomains.some(domain => errorString.includes(domain))
    
    // Check for Facebook-specific errors
    const isFacebookError = /facebook|fbcdn|FB\.|facebookexternalhit/i.test(errorString)
    
    // Only log if it's not an external error
    if (!isExternalError && !isFacebookError) {
      originalError.apply(console, args)
    }
  }

  // Global error handler for uncaught errors
  window.addEventListener('error', (event) => {
    const errorString = event.message || ''
    const filename = event.filename || ''
    
    // Check if error is from external source
    const isExternal = ignoredDomains.some(domain => 
      filename.includes(domain) || errorString.includes(domain)
    )
    
    const isFacebookError = /facebook|fbcdn|FB\./i.test(errorString + filename)
    
    // Prevent external errors from showing
    if (isExternal || isFacebookError) {
      event.preventDefault()
      return false
    }
  })

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason?.toString() || ''
    
    const isExternal = ignoredDomains.some(domain => reason.includes(domain))
    const isFacebookError = /facebook|fbcdn|FB\./i.test(reason)
    
    if (isExternal || isFacebookError) {
      event.preventDefault()
    }
  })
}

// Setup error handler before creating app
setupGlobalErrorHandler()

const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
app.use(router)
app.use(Vue3SocialSharing)

// Initialize auth state after pinia is available
const userStore = useUserStore()
userStore.initializeAuth()

// Load favicon from settings
const loadFavicon = async () => {
  try {
    const response = await settingsService.getSettings()
    
    if (response.success && response.settings) {
      // Check for favicon_url or other favicon-related fields
      const faviconField = response.settings.favicon_url || 
                          response.settings.site_favicon || 
                          response.settings.favicon ||
                          'favicon.ico'
                          
      const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement
      if (favicon) {
        favicon.href = getLogoUrl(faviconField)
      }
    }
  } catch (error) {
  }
}

// PWA Support
const initializePWA = () => {
  // Register service worker if available
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        
        // Handle service worker updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New content available
                showUpdateNotification()
              }
            })
          }
        })
      })
      .catch((registrationError) => {
      })
  }

  // Handle offline/online status
  window.addEventListener('online', () => {
    showConnectionStatus('online')
  })

  window.addEventListener('offline', () => {
    showConnectionStatus('offline')
  })

  // Handle PWA install prompt
  let deferredPrompt: any
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferredPrompt = e
    showInstallPrompt()
  })

  // Handle PWA install
  window.addEventListener('appinstalled', () => {
    hideInstallPrompt()
  })

  // Add install function to global scope
  ;(window as any).installPWA = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      if (outcome === 'accepted') {
      }
      deferredPrompt = null
    }
    hideInstallPrompt()
  }

  ;(window as any).dismissInstallPrompt = hideInstallPrompt
}

// Show update notification
const showUpdateNotification = () => {
  const updateToast = document.createElement('div')
  updateToast.innerHTML = `
    <div class="toast position-fixed top-0 end-0 m-3" style="z-index: 1055;">
      <div class="toast-header bg-primary text-white">
        <strong class="me-auto">Νέα Έκδοση Διαθέσιμη</strong>
        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="toast"></button>
      </div>
      <div class="toast-body">
        Υπάρχει νέα έκδοση της εφαρμογής. Κάντε refresh για ενημέρωση.
        <div class="mt-2">
          <button class="btn btn-sm btn-primary" onclick="window.location.reload()">Ανανέωση</button>
        </div>
      </div>
    </div>
  `
  document.body.appendChild(updateToast)
  
  // Initialize Bootstrap toast
  const toast = updateToast.querySelector('.toast')
  if (toast) {
    const bsToast = new (window as any).bootstrap.Toast(toast)
    bsToast.show()
  }
}

// Show connection status
const showConnectionStatus = (status: 'online' | 'offline') => {
  const statusToast = document.createElement('div')
  const isOnline = status === 'online'
  
  statusToast.innerHTML = `
    <div class="toast position-fixed top-0 end-0 m-3" style="z-index: 1055;">
      <div class="toast-header ${isOnline ? 'bg-success' : 'bg-warning'} text-white">
        <strong class="me-auto">${isOnline ? 'Συνδέθηκε' : 'Αποσυνδέθηκε'}</strong>
        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="toast"></button>
      </div>
      <div class="toast-body">
        ${isOnline 
          ? 'Η σύνδεση με το διαδίκτυο αποκαταστάθηκε' 
          : 'Η εφαρμογή λειτουργεί σε offline λειτουργία'
        }
      </div>
    </div>
  `
  document.body.appendChild(statusToast)
  
  const toast = statusToast.querySelector('.toast')
  if (toast) {
    const bsToast = new (window as any).bootstrap.Toast(toast, { delay: 3000 })
    bsToast.show()
    
    // Remove element after toast is hidden
    toast.addEventListener('hidden.bs.toast', () => {
      statusToast.remove()
    })
  }
}

// Show install prompt
const showInstallPrompt = () => {
  if (!localStorage.getItem('pwa-install-dismissed')) {
    const banner = document.getElementById('pwa-install-banner')
    if (banner) {
      setTimeout(() => {
        banner.classList.add('show')
      }, 3000)
    }
  }
}

// Hide install prompt
const hideInstallPrompt = () => {
  const banner = document.getElementById('pwa-install-banner')
  if (banner) {
    banner.classList.remove('show')
  }
  localStorage.setItem('pwa-install-dismissed', 'true')
}

app.mount('#app')

// Initialize after app is mounted
loadFavicon()
initializePWA()

// Push notifications: εγγραφή όταν ο χρήστης είναι logged in
router.afterEach(() => {
  const store = useUserStore()
  if (store.isAuthenticated) {
    subscribeToPush().catch(() => {})
  }
})
